import { useAbility as useCaslAbility } from '@casl/vue'
import { computed, inject } from 'vue'
import { CASL_SUBJECTS, CASL_ACTIONS } from '../utils/permissionParser'

/**
 * Enhanced useAbility composable with team-based helpers
 */
export function useAbility() {
  const ability = useCaslAbility()
  const updateAbilities = inject('updateAbilities', () => {})
  
  // Helper function to check team-specific permissions
  const canAccessTeam = (teamCode, action = 'access') => {
    if (!teamCode) return false
    return ability.can(action, `${teamCode}Parcels`)
  }
  
  // Team-specific parcel permissions
  const canCreateParcels = (teamCode) => {
    return ability.can(CASL_ACTIONS.CREATE, `${teamCode}Parcels`)
  }
  
  const canEditParcels = (teamCode) => {
    return ability.can(CASL_ACTIONS.UPDATE, `${teamCode}Parcels`) || 
           ability.can('edit', `${teamCode}Parcels`)
  }
  
  const canDeleteParcels = (teamCode) => {
    return ability.can(CASL_ACTIONS.DELETE, `${teamCode}Parcels`)
  }
  
  const canViewParcels = (teamCode) => {
    return ability.can(CASL_ACTIONS.VIEW, `${teamCode}Parcels`) ||
           ability.can(CASL_ACTIONS.READ, `${teamCode}Parcels`)
  }
  
  // Operation-specific permissions
  const canManageTeams = computed(() => {
    return ability.can(CASL_ACTIONS.MANAGE, CASL_SUBJECTS.TEAMS)
  })
  
  const canAssignUserTeams = computed(() => {
    return ability.can(CASL_ACTIONS.ASSIGN, CASL_SUBJECTS.USER_TEAMS)
  })
  
  const canCreatePackages = computed(() => {
    return ability.can(CASL_ACTIONS.CREATE, CASL_SUBJECTS.PACKAGES)
  })
  
  const canUpdateParcelStatus = computed(() => {
    return ability.can(CASL_ACTIONS.UPDATE, CASL_SUBJECTS.PARCEL_STATUS)
  })
  
  const canScanParcels = computed(() => {
    return ability.can(CASL_ACTIONS.SCAN, 'Parcels')
  })
  
  const canUploadImages = computed(() => {
    return ability.can(CASL_ACTIONS.UPLOAD, CASL_SUBJECTS.PARCEL_IMAGES)
  })
  
  const canViewAnalytics = computed(() => {
    return ability.can(CASL_ACTIONS.VIEW, CASL_SUBJECTS.TEAM_ANALYTICS) ||
           ability.can(CASL_ACTIONS.VIEW, CASL_SUBJECTS.ALL_ANALYTICS)
  })
  
  // JP (Je Prends) specific permissions
  const canManageClientOrders = computed(() => {
    return ability.can(CASL_ACTIONS.MANAGE, CASL_SUBJECTS.CLIENT_ORDERS)
  })
  
  const canProcessClientPurchases = computed(() => {
    return ability.can(CASL_ACTIONS.PROCESS, CASL_SUBJECTS.CLIENT_PURCHASES)
  })
  
  const canViewClientAnalytics = computed(() => {
    return ability.can(CASL_ACTIONS.VIEW, CASL_SUBJECTS.CLIENT_ANALYTICS)
  })
  
  // Admin checks
  const isAdmin = computed(() => {
    return ability.can(CASL_ACTIONS.MANAGE, CASL_SUBJECTS.ALL)
  })
  
  // Manager check
  const isManager = computed(() => {
    // Check if user has manager-specific permissions but not admin privileges
    return ability.can(CASL_ACTIONS.MANAGE, CASL_SUBJECTS.TEAMS) && 
           ability.can(CASL_ACTIONS.ASSIGN, CASL_SUBJECTS.USER_TEAMS) &&
           !ability.can(CASL_ACTIONS.MANAGE, CASL_SUBJECTS.ALL)
  })
  
  // Team access helper - extracts team codes from permissions
  const getAccessibleTeams = () => {
    const teams = []
    
    // Check all permissions and extract team codes dynamically
    const rules = ability.rules || []
    
    for (const rule of rules) {
      // Look for team-specific subjects like 'GB-Parcels', 'GD-Parcels', 'JP-Parcels'
      if (rule.subject && rule.subject.includes('-') && rule.subject.endsWith('Parcels')) {
        // Extract team code from subjects like 'GB-Parcels' -> 'GB'
        const teamCode = rule.subject.split('-')[0]
        if (teamCode && ['GB', 'GD', 'JP'].includes(teamCode.toUpperCase())) {
          const normalizedTeam = teamCode.toUpperCase()
          if (!teams.includes(normalizedTeam)) {
            teams.push(normalizedTeam)
          }
        }
      }
      // Also check for other team-specific subjects like 'GB-Packages', 'GD-Analytics', etc.
      else if (rule.subject && rule.subject.includes('-')) {
        const teamCode = rule.subject.split('-')[0]
        if (teamCode && ['GB', 'GD', 'JP'].includes(teamCode.toUpperCase())) {
          const normalizedTeam = teamCode.toUpperCase()
          if (!teams.includes(normalizedTeam)) {
            teams.push(normalizedTeam)
          }
        }
      }
    }
    
    return teams
  }
  
  // Generic permission checker with team context
  const canPerformAction = (action, subject, teamCode = null) => {
    if (teamCode) {
      return ability.can(action, `${teamCode}${subject}`)
    }
    return ability.can(action, subject)
  }
  
  // Bulk operation permissions
  const canBulkUpdate = computed(() => {
    return ability.can('bulk-update', 'Parcels') || isAdmin.value
  })
  
  const canMassDelete = computed(() => {
    return ability.can('mass-delete', 'Parcels') || isAdmin.value
  })
  
  // UI helper for team badge colors
  const getTeamBadgeColor = (teamCode) => {
    const colors = {
      'GB': 'error',    // Red for Going Beyond
      'GD': 'info',     // Teal for Godana  
      'JP': 'primary'   // Blue for Je Prends
    }
    return colors[teamCode] || 'secondary'
  }
  
  // UI helper for team display names
  const getTeamDisplayName = (teamCode) => {
    const names = {
      'GB': 'Going Beyond',
      'GD': 'Godana',
      'JP': 'Je Prends'
    }
    return names[teamCode] || teamCode
  }
  
  // Check if user has any team access
  const hasAnyTeamAccess = computed(() => {
    return getAccessibleTeams().length > 0
  })
  
  // Check if user has access to multiple teams
  const hasMultiTeamAccess = computed(() => {
    return getAccessibleTeams().length > 1
  })
  
  return {
    // Original CASL ability and can function
    ability,
    can: ability.can.bind(ability),
    
    // Update function
    updateAbilities,
    
    // Team access
    canAccessTeam,
    getAccessibleTeams,
    hasAnyTeamAccess,
    hasMultiTeamAccess,
    
    // Team-specific parcel permissions
    canCreateParcels,
    canEditParcels,
    canDeleteParcels,
    canViewParcels,
    
    // Operation permissions
    canManageTeams,
    canAssignUserTeams,
    canCreatePackages,
    canUpdateParcelStatus,
    canScanParcels,
    canUploadImages,
    canViewAnalytics,
    canBulkUpdate,
    canMassDelete,
    
    // JP specific permissions
    canManageClientOrders,
    canProcessClientPurchases,
    canViewClientAnalytics,
    
    // Admin checks
    isAdmin,
    isManager,
    
    // Generic checker
    canPerformAction,
    
    // UI helpers
    getTeamBadgeColor,
    getTeamDisplayName
  }
}

